/* istanbul ignore file - added ignore file 
because I ended up adding a lot of comments to the code, this component is tested e2e 
and it contains a lot of functions and conditional logic */
import React, { useState } from 'react'

import Carousel from '@components/CompactTripCard/Carousel'
import FareClassCard from '@components/CompactTripCard/FareClassCard'
import { getFareClasses } from '@lib/fareClass'
import { useTranslation } from '@lib/i18n'

import '@components/CompactTripCard/FareClassesExpanded/styles.scss'

interface FareClassesExpandedProps {
  connection: Connection
  onSelect?: (fareClass: string) => void
}

const FareClassesExpanded = ({ connection, onSelect }: FareClassesExpandedProps) => {
  const { t } = useTranslation()

  const [currentPage, setCurrentPage] = useState(0)

  const { secondClassFares, firstClassFares } = getFareClasses(connection.fares)

  const maxItemsPerSection = 3
  const totalFares = secondClassFares.length + firstClassFares.length
  const showAllOnSinglePage = totalFares <= 5

  const effectiveMaxItemsPerSection = showAllOnSinglePage ? secondClassFares.length : maxItemsPerSection
  const effectiveMaxItemsPerSectionFirst = showAllOnSinglePage ? firstClassFares.length : maxItemsPerSection

  const secondClassPages = showAllOnSinglePage ? 1 : Math.ceil(secondClassFares.length / maxItemsPerSection)
  const firstClassPages = showAllOnSinglePage ? 1 : Math.ceil(firstClassFares.length / maxItemsPerSection)
  const totalPages = Math.max(secondClassPages, firstClassPages, 1)

  const getVisibleFares = (allFares: Fare[], page: number, maxItems: number) => {
    if (showAllOnSinglePage) return allFares
    const startIndex = page * maxItems
    const endIndex = startIndex + maxItems
    return allFares.slice(startIndex, endIndex)
  }

  const visibleSecondClassFares = getVisibleFares(secondClassFares, currentPage, effectiveMaxItemsPerSection)
  const visibleFirstClassFares = getVisibleFares(firstClassFares, currentPage, effectiveMaxItemsPerSectionFirst)

  const hasVisibleSecondClass = visibleSecondClassFares.length > 0
  const hasVisibleFirstClass = visibleFirstClassFares.length > 0

  const getGridTemplate = () => {
    if (!hasVisibleSecondClass && hasVisibleFirstClass) return '1fr'
    if (hasVisibleSecondClass && !hasVisibleFirstClass) return '1fr'
    if (hasVisibleSecondClass && hasVisibleFirstClass) {
      const secondFraction = Math.max(1, visibleSecondClassFares.length)
      const firstFraction = Math.max(1, visibleFirstClassFares.length)
      return `${secondFraction}fr ${firstFraction}fr`
    }
    return '1fr'
  }

  const gridTemplate = getGridTemplate()

  return (
    <div className="fare-classes-container">
      <div
        className="fare-headers"
        style={{
          gridTemplateColumns: gridTemplate,
        }}
      >
        {hasVisibleSecondClass && <div className="header-2nd-class">{t('compactTripCard.secondClass')}</div>}
        {hasVisibleFirstClass && <div className="header-1st-class">{t('compactTripCard.firstClass')}</div>}
      </div>
      <Carousel
        secondClassFares={secondClassFares}
        firstClassFares={firstClassFares}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      >
        <div
          className="fare-classes-grid"
          style={{
            gridTemplateColumns: gridTemplate,
          }}
        >
          {hasVisibleSecondClass && (
            <div className={`second-class-section${visibleSecondClassFares.length === 1 ? ' single-card' : ''}`}>
              {visibleSecondClassFares.map(fare => (
                <div
                  key={fare.id}
                  className={`fare-card-grid-item${visibleSecondClassFares.length === 1 ? ' single-card' : ''}`}
                >
                  <FareClassCard connection={connection} fare={fare} onSelect={onSelect} />
                </div>
              ))}
            </div>
          )}
          {hasVisibleFirstClass && (
            <div className={`first-class-section${visibleFirstClassFares.length === 1 ? ' single-card' : ''}`}>
              {visibleFirstClassFares.map(fare => (
                <div
                  key={fare.id}
                  className={`fare-card-grid-item${visibleFirstClassFares.length === 1 ? ' single-card' : ''}`}
                >
                  <FareClassCard connection={connection} fare={fare} onSelect={onSelect} />
                </div>
              ))}
            </div>
          )}
        </div>
      </Carousel>
    </div>
  )
}

export default FareClassesExpanded
